import { formatMoney } from "helpers";
import { monifyInputField } from "helpers/monifyInputField";
import "react-responsive-pagination/themes/classic.css";
import moment from "moment";
import { Tooltip } from "react-tooltip";
import React, { useMemo, useState } from "react";
import { truncate } from "lodash";

import CustomTable from "components/common/CustomTable";
import EmptyStateComp from "components/common/EmptyStateComp";
import SelectInput from "components/common/InputField/SelectInput";
import TextInput from "components/common/InputField/TextInput";
import SynthTooltip from "components/common/SynthTooltip";
import ResponsivePaginationComponent from "react-responsive-pagination";
import useGetDashboardConfigListV2 from "hooks/Dashboard/useGetDashboardConfigListV2";
import { ForecastMethodEnum } from "./enums/ForecastMethodEnum";
import clsx from "clsx";
import { ForecastConfigurationTableContentProps } from "./types/forcastConfig";
import FloatingActionBar from "components/common/FloatingActionBar";
import Button from "components/common/Button";
import TableBodyItem from "components/common/TableBodyItem";
import Dropdown from "components/common/Dropdown";
import ForecastNewProductModal from "./ForecastNewProductModal";
import { useNavigate } from "react-router-dom";

type ForecastConfigurationSingleTableProps = ForecastConfigurationTableContentProps;

const ForecastConfigurationSingleTable = (props: ForecastConfigurationSingleTableProps) => {
    const {
        categoryFilter,
        channelFilter,
        productIds,
        chartFilter,
        handleConfigChange,
        monthlyConfigChange,
        weeklyConfigChange,
        forecastMethodOptions,
        activePeriod,
        monthlyTab,
    } = props;
    const navigate = useNavigate();

    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [isNewProductModalOpen, setIsNewProductModalOpen] = useState<boolean>(false);

    const [page, setPage] = useState<number>(1);

    const handleScroll = (e) => {
        if (e.target.scrollLeft > 0) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const {
        data: configList,
        isFetching: configLoading,
        pagination: configPagination,
        refetch: refetchConfigList,
    } = useGetDashboardConfigListV2(
        useMemo(
            () => ({
                params: {
                    page: page,
                    dateRange: "yearly",
                    productId: productIds?.join(","),
                    channel: channelFilter,
                    category: categoryFilter,
                },
            }),
            [productIds, channelFilter, categoryFilter, page]
        )
    );

    const configureForecastTableBody = configList?.map((product, idx) => {
        const currMonth = moment()?.format("MMM-YYYY");
        const currentKeyFormat = monthlyTab?.[activePeriod]?.split(" ")?.join("-");
        const prevKeyFormat = moment(currentKeyFormat)?.subtract(1, "months")?.format("MMM-YYYY");

        const currentConfigObj = product?.configs?.find?.((item) => {
            return chartFilter === "yearly" ? item?.[currentKeyFormat] : item?.[currMonth];
        });

        const currentConfigValues =
            chartFilter === "yearly"
                ? currentConfigObj?.[currentKeyFormat]?.[0]
                : currentConfigObj?.[currMonth]?.[activePeriod];

        const currForecastData =
            chartFilter === "yearly"
                ? product?.estinmatedSalesForecast?.monthlyForecast?.find?.((item) => {
                      return item?.yearMonth === currentKeyFormat;
                  })
                : product?.estinmatedSalesForecast?.weeklyForecast?.filter?.((item) => {
                      return item?.yearMonth === currMonth;
                  });

        const prevForecastData =
            chartFilter === "yearly"
                ? product?.estinmatedSalesForecast?.monthlyForecast?.find?.((item) => {
                      return item?.yearMonth === prevKeyFormat;
                  })
                : product?.estinmatedSalesForecast?.weeklyForecast?.filter?.((item) => {
                      return item?.yearMonth === currMonth;
                  });

        const currDemandForecast =
            chartFilter === "yearly"
                ? String(Math.round(currForecastData?.forecastedSale || 0))
                : String(Math.round(currForecastData?.[activePeriod]?.forecastedSale || 0));

        const prevDemandForecast =
            chartFilter === "yearly"
                ? String(Math.round(prevForecastData?.forecastedSale || 0))
                : String(Math.round(prevForecastData?.[activePeriod - 1]?.forecastedSale || 0));

        const currStockLevel = Math.round(
            Number(currentConfigValues?.monthlyDemand?.stockLevel) ||
                Number(product?.inventory?.productStockDetails?.stockLevel)
        );

        const stockDeficit =
            chartFilter === "yearly"
                ? String(
                      Math.round(currForecastData?.forecastedSale || 0) -
                          Number(currStockLevel || 0)
                  )
                : String(
                      Math.round(currForecastData?.[activePeriod]?.forecastedSale || 0) -
                          Number(currStockLevel || 0)
                  );

        const revenueForecast =
            chartFilter === "yearly"
                ? Math.round(currForecastData?.forecastedSale || 0) *
                  (product?.inventory?.salesPrice?.amount || 0)
                : Math.round(currForecastData?.[activePeriod]?.forecastedSale || 0) *
                  (product?.inventory?.salesPrice?.amount || 0);

        const configCopy = [
            ...(chartFilter === "yearly" ? monthlyConfigChange : weeklyConfigChange),
        ];

        const changedConfig = configCopy.find((conf) => {
            return (
                conf?.[chartFilter === "yearly" ? monthlyTab?.[activePeriod] : currMonth]
                    ?.inventoryId === product?.inventory?._id
            );
        });
        const changeValue = Math.round(
            chartFilter === "yearly"
                ? changedConfig?.[monthlyTab?.[activePeriod]]?.monthlyDemand?.quantity
                : changedConfig?.[currMonth]?.weeklyDemand?.[activePeriod]?.quantity
        );
        const defaultValue = Math.round(
            chartFilter === "yearly"
                ? currentConfigValues?.monthlyDemand?.quantity || ""
                : currentConfigValues?.weeklyDemand?.quantity || ""
        );

        const adjustedRevenueForecast =
            Math.round(Number(changeValue || defaultValue || 0)) *
            (product?.inventory?.salesPrice?.amount || 0);

        const adjustedStockDeficit = String(
            Math.round(Number(changeValue || defaultValue || 0)) - Number(currStockLevel || 0)
        );

        const changeForecastMethodValue =
            chartFilter === "yearly"
                ? changedConfig?.[monthlyTab?.[activePeriod]]?.forecastMethod
                : changedConfig?.[currMonth]?.forecastMethod;

        const defaultForecastMethodValue =
            product?.inventory?.forecastSettings?.[channelFilter]?.forecastMethod ||
            ForecastMethodEnum.synth;

        const selectedForecastMethod = forecastMethodOptions.find(
            (item) => item.value === (changeForecastMethodValue || defaultForecastMethodValue)
        );

        const synthGrowthRate =
            Boolean(Number(currDemandForecast)) && Boolean(Number(prevDemandForecast))
                ? Math.round(
                      ((Number(currDemandForecast || 0) - Number(prevDemandForecast || 0)) /
                          ((Number(currDemandForecast || 0) + Number(prevDemandForecast || 0)) /
                              2)) *
                          100
                  )
                : 0;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <a data-tooltip-id={`product-column-${idx}`}>
                                <p className="text-slate-500 text-xs ">
                                    SKU {product?.inventory?.sku}
                                </p>

                                <p className="">
                                    {truncate(product?.inventory?.productName, {
                                        length: 20,
                                    })}
                                </p>
                            </a>
                            <Tooltip
                                id={`product-column-${idx}`}
                                place={"top"}
                                style={{
                                    maxWidth: "250px",
                                    zIndex: 9999,
                                    background: "#fff",
                                    borderRadius: "6px",
                                    border: "1px solid #E2E8F0",
                                    color: "#1E293B",
                                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                }}
                            >
                                <p className="text-slate-500 text-xs ">
                                    SKU {product?.inventory?.sku}
                                </p>

                                <p className="">{product?.inventory?.productName}</p>
                            </Tooltip>
                        </div>
                    </div>
                ),
                isItemChecked: selectedProducts?.some(
                    (item) => item?._id === product?.inventory?._id
                ),
                onCheckBoxClick: () => {
                    setSelectedProducts((prev) =>
                        prev?.some((item) => item?._id === product?.inventory?._id)
                            ? prev?.filter((item) => item?._id !== product?.inventory?._id)
                            : [...prev, product?.inventory]
                    );
                },
            },

            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p className="">{formatMoney().format(currStockLevel) || "N/A"}</p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p className="">
                            {synthGrowthRate && !isNaN(synthGrowthRate)
                                ? synthGrowthRate + "%"
                                : "N/A"}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600 w-full py-2`}
                    >
                        <SelectInput
                            value={changeForecastMethodValue || defaultForecastMethodValue}
                            name="forecastMethod"
                            idx={idx}
                            toolTipController={{
                                content: selectedForecastMethod ? (
                                    <div className="flex flex-col gap-1 font-rocGroteskMedium">
                                        <p>{selectedForecastMethod.label}</p>
                                        <p className="text-slate-400">
                                            {selectedForecastMethod.description}
                                        </p>
                                    </div>
                                ) : (
                                    "testing"
                                ),
                                className: "!w-[500px] !max-w-[374px] !p-[12px] !rounded-[8px]",
                                place: "top",
                                style: { maxWidth: "250px", zIndex: 9999 },
                            }}
                            placeholder={"Select forecast method"}
                            isRequired={true}
                            handleChange={(_, value) => {
                                handleConfigChange(
                                    {
                                        adjustedDemandForecast: monifyInputField(
                                            changeValue >= 0 ? changeValue : defaultValue
                                        ),
                                        forecastMethod: value,
                                    },
                                    product?.inventory,
                                    currentConfigObj?.[currMonth]
                                );
                            }}
                            dropdownClassName="!w-[500px] !max-w-[500px] !p-0"
                            // inputContainerClassName="!p-0"
                            // optionItemClassName="!p-0"
                            optionItemContainerClassName="!p-0"
                            activeOptionItemClassName="!hidden"
                            activeOptionItemContainerClassName="!p-0"
                            optionItemClassName="!p-0"
                            dropdownOptions={forecastMethodOptions.map((item) => ({
                                label: (
                                    <div
                                        className={clsx(
                                            "flex flex-col w-full w-[500px] gap-1 px-[16px] py-[10px]",
                                            item.value ===
                                                (changeForecastMethodValue ||
                                                    defaultForecastMethodValue) && "bg-[#F1F5F9]"
                                        )}
                                    >
                                        <p>{item.label}</p>
                                        <p className="text-xs text-slate-500">{item.description}</p>
                                    </div>
                                ),
                                value: item.value,
                            }))}
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600 w-full py-2`}
                    >
                        <TextInput
                            name={"adjustedDemandForecast"}
                            type={"text"}
                            onWheel={(e: any) => e.target.blur()}
                            value={monifyInputField(changeValue >= 0 ? changeValue : defaultValue)}
                            onChange={(e) => {
                                handleConfigChange(
                                    {
                                        adjustedDemandForecast: monifyInputField(e.target.value),
                                        forecastMethod:
                                            changeForecastMethodValue || defaultForecastMethodValue,
                                    },
                                    product?.inventory,
                                    currentConfigObj?.[currMonth]
                                );
                            }}
                            placeholder={"Adjusted Demand"}
                            required={true}
                            onKeyDown={(evt) =>
                                ["e", "E", "+", "-", ".", "ArrowUp", "ArrowDown"].includes(
                                    evt.key
                                ) && evt.preventDefault()
                            }
                            pattern={"^[0-9]+$"}
                            title={"must be digits"}
                        />
                    </div>
                ),
                tableDataDivClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p className="">{formatMoney().format(currDemandForecast)}</p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p
                            className={`${
                                Number(currStockLevel || 0) >= Number(changeValue || defaultValue)
                                    ? ""
                                    : "text-r-55"
                            }`}
                        >
                            {Number(currStockLevel || 0) >= Number(changeValue || defaultValue)
                                ? "0"
                                : formatMoney()?.format(adjustedStockDeficit)}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p
                            className={`${
                                Number(currStockLevel || 0) >= Number(currDemandForecast)
                                    ? ""
                                    : "text-r-55"
                            }`}
                        >
                            {Number(currStockLevel || 0) >= Number(currDemandForecast)
                                ? "N/A"
                                : formatMoney()?.format(stockDeficit)}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p className="">
                            {Boolean(adjustedRevenueForecast)
                                ? formatMoney()?.format(adjustedRevenueForecast)
                                : "N/A"}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p className="">
                            {revenueForecast ? formatMoney()?.format(revenueForecast) : "N/A"}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <p
                            className={`${
                                Number(currStockLevel || 0) >= Number(currDemandForecast)
                                    ? ""
                                    : "text-r-55"
                            }`}
                        >
                            {Number(currStockLevel || 0) >= Number(currDemandForecast)
                                ? "N/A"
                                : formatMoney()?.format(stockDeficit)}
                        </p>
                    </div>
                ),
            },
            {
                content: (
                    <TableBodyItem className="flex justify-center items-center">
                        <Dropdown
                            dropdown={<i className="ri-more-2-fill text-lg text-slate-500"></i>}
                            dropdownClassName={"!w-full !mb-10 !-mt-7"}
                            dropdownContainerClasses={`shadow-cardShadow-2 z-[1400] border-0 rounded !max-h-fit right-[0px] right-[20px]`}
                            name={"dropdown"}
                            dropdownItemsClasses="!bg-transparent "
                            dropdownOptions={[
                                {
                                    label: "Use this product data to forecast a new product",
                                    value: "NewProduct",
                                    action: () => {
                                        setSelectedProducts([product?.inventory]);
                                        setIsNewProductModalOpen(true);
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                                {
                                    label: "Create sales target",
                                    value: "SalesTarget",
                                    action: () => {
                                        setSelectedProducts([product?.inventory]);

                                        navigate(
                                            "/dashboard/forecast?agent=TargetAlly&updated=false",
                                            {
                                                state: {
                                                    internalNavigation: true,
                                                    selectedProducts: [product?.inventory],
                                                },
                                            }
                                        );
                                    },
                                    labelClassName: "!text-slate-600 !text-sm",
                                },
                            ]}
                        />
                    </TableBodyItem>
                ),
                widthClass: "!w-[50px]",
            },
        ];
    });

    const configureForecastTableHeader = [
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Product</span>

                    <SynthTooltip
                        content={"Displays the SKU and name of the product being tracked."}
                        divId={"product-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            // onSelectAll: () => {
            //     setSelectedProducts(
            //         selectedProducts?.length === configList?.length
            //             ? []
            //             : configList?.map((item) => item?.inventory?._id)
            //     );
            // },
            // isAllChecked: selectedProducts?.length === configList?.length,
            widthClass: "w-[200px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Current stock level</span>

                    <SynthTooltip
                        content={
                            "Shows the current quantity of the product available in your inventory."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                        position="bottom"
                    />
                </div>
            ),
            widthClass: "w-[200px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Synth's Growth rate</span>

                    <SynthTooltip
                        content={
                            "Indicates the projected percentage growth rate for the product as calculated by Synth."
                        }
                        divId={"synth-growth-rate-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[200px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Forecast Method</span>

                    <SynthTooltip
                        content={"Choose how you want synth to generate your forecast."}
                        divId={"adjusted-demand-forecast-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[230px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Adjusted demand forecast</span>

                    <SynthTooltip
                        content={
                            "Represents the user-adjusted forecast for product demand based on expected sales."
                        }
                        divId={"adjusted-demand-forecast-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[230px]",
        },

        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Synth's demand forecast</span>

                    <SynthTooltip
                        content={
                            "Displays the demand forecast automatically generated by Synth based on historical data and trends."
                        }
                        divId={"synth-demand-forecast-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[210px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Adjusted Stock deficit</span>

                    <SynthTooltip
                        content={
                            "Shows the shortfall in stock based on the adjusted demand forecast."
                        }
                        divId={"adjusted-stock-deficit-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[200px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Synth's Stock deficit</span>

                    <SynthTooltip
                        content={
                            "Indicates the stock deficit calculated by Synth if current trends continue."
                        }
                        divId={"synth-stock-deficit-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[200px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">
                        Adjusted revenue forecast
                    </span>

                    <SynthTooltip
                        content={
                            "Represents the projected revenue from this product based on your adjusted demand forecast."
                        }
                        divId={"adjusted-revenue-forecast-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[230px]",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium ">Synth's Revenue forecast</span>

                    <SynthTooltip
                        content={
                            "Displays the revenue forecast calculated by Synth using its demand forecast."
                        }
                        divId={"synth-revenue-forecast-column"}
                        icon={<i className="ri-information-fill text-slate-300 text-sm"></i>}
                    />
                </div>
            ),
            widthClass: "w-[210px]",
        },
        { title: "Replenishment due", widthClass: "w-[200px]" },
        {
            title: "",
            widthClass: "w-[50px]",
        },
    ];

    return (
        <div>
            <div className="mt-4">
                <div className="overflow-x-scroll" onScroll={handleScroll}>
                    <CustomTable
                        tableBody={configureForecastTableBody}
                        tableHeader={configureForecastTableHeader}
                        isScrolled={isScrolled}
                        isLoading={configLoading}
                        isScrollable
                        isCellBordered
                        scrollWithin
                        isCellSelectable={true}
                        isInifiniteScroll={false}
                        tableContainerClass="!max-h-[427px]"
                        headerContainerClass="!bg-slate-50"
                        headerItemClass="font-rocGroteskMedium !text-xs !text-slate-700"
                        tableClass="!border !border-slate-200 cursor-pointer"
                        bodyItemClass="!border-slate-200 !cursor-default hover:!bg-slate-50"
                        renderEmptyTable={() => (
                            <EmptyStateComp
                                title="No products available"
                                description={`There are no products available `}
                                icon={
                                    <i className="ri-archive-2-line text-[48px] text-slate-400"></i>
                                }
                            />
                        )}
                    />
                </div>
            </div>

            <div className="flex justify-end mt-5">
                <ResponsivePaginationComponent
                    current={Number(configPagination?.currentPage)}
                    total={Number(configPagination?.noOfPages)}
                    maxWidth={350}
                    onPageChange={(page) => {
                        setPage(page);
                    }}
                />
            </div>

            <FloatingActionBar
                totalSelected={selectedProducts?.length}
                open={selectedProducts?.length > 0}
                onClose={() => {
                    setSelectedProducts([]);
                }}
                title="Product Selected"
            >
                <div className="flex gap-2">
                    <Button
                        btnText="Forecast New Product"
                        btnClassname="!bg-[#F4F5F7] cursor-pointer w-full !text-[13px] !px-[12px] !py-2.5 rounded-[5px] !h-[42px] !whitespace-nowrap"
                        onClick={() => {
                            setIsNewProductModalOpen(true);
                        }}
                    />
                    <Button
                        btnText="Create Sales target"
                        btnClassname="!bg-[#F4F5F7] cursor-pointer w-full !text-[13px] !px-[12px] !py-2.5 rounded-[5px] !h-[42px] !whitespace-nowrap"
                        onClick={() => {
                            navigate("/dashboard/forecast?agent=TargetAlly&updated=false", {
                                state: {
                                    internalNavigation: true,
                                    selectedProducts: selectedProducts,
                                },
                            });
                        }}
                    />
                </div>
            </FloatingActionBar>

            {isNewProductModalOpen && (
                <ForecastNewProductModal
                    selectedSimilarProducts={selectedProducts?.map((item) => ({
                        _id: item?._id,
                        productName: item?.productName,
                        sku: item?.sku,
                    }))}
                    open={isNewProductModalOpen}
                    closeModal={() => {
                        setSelectedProducts([]);
                        refetchConfigList?.();
                        setIsNewProductModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default ForecastConfigurationSingleTable;
